import type {
  FavoriteLabelSource,
  FavoritePageDescriptor,
} from '@/types/favorites';
import type { NavigationItem } from '@/ui/Layout/Sidebar/NavigationItems/navigationItems';
import type { FavoriteMetadata } from '@/utils/pageMetadata/PageMetadataContext';
import {
  getFavoritePathname,
  normalizeFavoritePath,
  stripFavoriteSearch,
} from '@/utils/favorites/path';

const FAVORITE_LABEL_DELIMITER = ' / ';

function formatSegment(segment: string): string {
  const decodedSegment = decodeURIComponent(segment);
  return decodedSegment
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function buildPathFallbackLabels(pathname: string): string[] {
  const pathSegments = pathname.split('/').filter(Boolean);
  const relevantSegments = pathSegments.slice(-2);

  if (!relevantSegments.length) {
    return ['Home'];
  }

  return relevantSegments.map(formatSegment);
}

function getDisplayLabel(labels: string[]): string {
  return labels.slice(-2).join(FAVORITE_LABEL_DELIMITER);
}

function hasCustomFavoriteMetadata(
  favoriteMetadata?: FavoriteMetadata
): favoriteMetadata is FavoriteMetadata {
  return Boolean(
    favoriteMetadata?.labels?.length ||
      favoriteMetadata?.displayLabel ||
      favoriteMetadata?.fullLabel
  );
}

function getNavigationLabels(
  items: NavigationItem[],
  pathname: string,
  trail: string[] = []
): string[] | null {
  for (const item of items) {
    const nextTrail = [...trail, item.label];

    if (item.url && stripFavoriteSearch(item.url) === pathname) {
      return nextTrail;
    }

    if (item.items?.length) {
      const result = getNavigationLabels(item.items, pathname, nextTrail);
      if (result) {
        return result;
      }
    }
  }

  return null;
}

export function buildFavoritePageDescriptor({
  path,
  navigationItems,
  pageTitle,
  crumbLabels = [],
  favoriteMetadata,
}: {
  path: string;
  navigationItems: NavigationItem[];
  pageTitle?: string;
  crumbLabels?: string[];
  favoriteMetadata?: FavoriteMetadata;
}): FavoritePageDescriptor {
  const normalizedPath = normalizeFavoritePath(path);
  const pathname = getFavoritePathname(normalizedPath);
  const navigationLabels = getNavigationLabels(navigationItems, pathname);

  let labels: string[] = [];
  let source: FavoriteLabelSource = 'path';
  let fullLabel = '';
  let displayLabel = '';

  if (hasCustomFavoriteMetadata(favoriteMetadata)) {
    labels = (favoriteMetadata.labels || []).filter(Boolean);
    fullLabel =
      favoriteMetadata.fullLabel || labels.join(FAVORITE_LABEL_DELIMITER);
    displayLabel =
      favoriteMetadata.displayLabel ||
      (labels.length ? getDisplayLabel(labels) : fullLabel);
    source = 'page';
  } else if (crumbLabels.length) {
    labels = crumbLabels;
    if (pageTitle && !crumbLabels.includes(pageTitle)) {
      labels = [...crumbLabels, pageTitle];
    }
    source = 'page';
  } else if (navigationLabels?.length) {
    labels = navigationLabels;
    source = 'navigation';
  } else if (pageTitle) {
    labels = [pageTitle];
    source = 'page';
  } else {
    labels = buildPathFallbackLabels(pathname);
  }

  if (!fullLabel) {
    fullLabel = labels.join(FAVORITE_LABEL_DELIMITER);
  }

  if (!displayLabel) {
    displayLabel = getDisplayLabel(labels);
  }

  return {
    path: normalizedPath,
    pathname,
    fullLabel,
    displayLabel,
    source,
  };
}
